/**
 * 확정 처방 내역 상세 (조회 전용).
 * admin-ruda-web `prescriptionDetailsInfo.js` 포팅.
 */
import { useFinalizedPrescriptionDetail } from '@/entities/prescription/hooks/usePrescriptions';
import { buildPrescriptionImageFileName } from '@/pages/prescriptions/prescriptionExportUtils';
import {
  formatCommissionRate,
  formatNumber,
  formatQuantity,
  formatYearMonth,
  getApprovalStatusBadgeVariant,
  getApprovalStatusLabel,
} from '@/pages/prescriptions/prescriptionUtils';
import { DownloadWithPreview } from '@/shared/components/DownloadWithPreview';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import React, { useEffect } from 'react';

export const PrescriptionHistoryDetailPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const router = useRouter();
  const { prescriptionId: idParam } = useParams({ strict: false }) as {
    prescriptionId?: string;
  };
  const prescriptionId = Number(idParam);

  const { data, isLoading, isError, error } = useFinalizedPrescriptionDetail(
    Number.isFinite(prescriptionId) ? prescriptionId : null,
  );

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  /** 레거시 QuitButton — 이전 화면(목록 URL 쿼리 포함) 또는 목록 */
  const navigateBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    navigate({ to: ROUTE_PATHS.PRESCRIPTION_HISTORY } as never);
  };

  if (!Number.isFinite(prescriptionId)) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        잘못된 처방 ID입니다.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="처방 상세를 불러오는 중..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        처방 정보를 찾을 수 없습니다.
      </div>
    );
  }

  // 레거시: price * outHospitalQuantity
  const prescriptionAmount =
    (data.price ?? 0) * (data.outHospitalQuantity ?? 0);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="확정 처방내역 상세"
        description="확정된 처방 내역 조회"
        actions={
          <Button variant="outline" onClick={navigateBack}>
            나가기
          </Button>
        }
      />

      <Card>
        <CardContent className="space-y-6 p-6">
          {/* 레거시 Row1 */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Field label="업체명">
              <Input disabled value={data.companyName || '-'} />
            </Field>
            <Field label="사용자 이메일">
              <Input disabled value={data.userEmail || '-'} />
            </Field>
            <Field label="전화번호">
              <Input disabled value={data.phoneNumber || '-'} />
            </Field>
          </div>

          {/* 레거시 Row2 */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Field label="병의원명">
              <Input disabled value={data.clinicName || '-'} />
            </Field>
            <Field label="처방내역 고유번호">
              <Input disabled value={data.prescriptionImageCode || '-'} />
            </Field>
            <DownloadWithPreview
              label="처방내역 이미지"
              src={data.prescriptionImageUrl}
              downloadName={buildPrescriptionImageFileName(data, {
                withExtension: false,
              })}
            />
          </div>

          {/* 레거시 Row3 */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Field label="처방 등록 년월">
              <Input
                disabled
                value={formatYearMonth(data.prescriptionRegisteredYearMonth)}
              />
            </Field>
            <Field label="처방 년월">
              <Input
                disabled
                value={formatYearMonth(data.prescriptionYearMonth)}
              />
            </Field>
            <Field label="정산 년월">
              <Input
                disabled
                value={formatYearMonth(data.settlementYearMonth)}
              />
            </Field>
          </div>

          {/* 레거시 Row4 */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Field label="제약사명">
              <Input disabled value={data.pharmaCompanyName || '-'} />
            </Field>
            <Field label="의약품 코드">
              <Input disabled value={data.drugCode || '-'} />
            </Field>
            <Field label="의약품명">
              <Input disabled value={data.drugName || '-'} />
            </Field>
            <Field label="약가">
              <Input disabled value={formatNumber(data.price)} />
            </Field>
          </div>

          {/* 레거시 Row5 */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Field label="원외 처방량">
              <Input
                disabled
                value={formatQuantity(data.outHospitalQuantity)}
              />
            </Field>
            <Field label="원내 사용량">
              <Input
                disabled
                value={formatQuantity(data.inHospitalQuantity)}
              />
            </Field>
            <Field label="처방 금액">
              <Input disabled value={formatNumber(prescriptionAmount)} />
            </Field>
          </div>

          {/* 레거시 Row6 */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Field label="정산 수수료율">
              <Input
                disabled
                value={formatCommissionRate(data.commissionRate)}
              />
            </Field>
            <Field label="정산 예상 금액">
              <Input
                disabled
                value={
                  data.estimatedAmount != null
                    ? Math.floor(data.estimatedAmount).toLocaleString('ko-KR')
                    : '-'
                }
              />
            </Field>
            <Field label="처방내역 승인 상태">
              <div className="flex h-10 items-center">
                <Badge
                  variant={getApprovalStatusBadgeVariant(data.approvalStatus)}
                >
                  {getApprovalStatusLabel(data.approvalStatus)}
                </Badge>
              </div>
            </Field>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
